/**
 * Time zones a provisioning template can set on its phones (owner, 17 September 2026).
 *
 * Keyed by IANA name, which is what accounts store. Each carries the offset and zone name as
 * Yealink spells them (Time and Date, Yealink admin guides): the phone looks its daylight saving
 * rules up by that name, so it must match Yealink's list exactly. Where Yealink's list is out of
 * date the entry uses a zone with the right offset instead (Namibia has been UTC+2 since 2017,
 * Turkey UTC+3 since 2016).
 */
export interface PhoneTimeZone {
  id: string;
  label: string;
  /** Minutes east of UTC, standard time. */
  offset_minutes: number;
  yealink: { offset: string; name: string };
}

const z = (id: string, label: string, offset: string, name: string): PhoneTimeZone => {
  const [h, m = '0'] = offset.slice(1).split(':');
  const minutes = Number(h) * 60 + Number(m);
  return { id, label, offset_minutes: offset.startsWith('-') ? -minutes : minutes, yealink: { offset, name } };
};

export const PHONE_TIME_ZONES: readonly PhoneTimeZone[] = [
  z('Africa/Johannesburg', 'South Africa (Johannesburg)', '+2', 'Pretoria'),
  z('Africa/Maseru', 'Lesotho (Maseru)', '+2', 'Pretoria'),
  z('Africa/Mbabane', 'Eswatini (Mbabane)', '+2', 'Pretoria'),
  z('Africa/Gaborone', 'Botswana (Gaborone)', '+2', 'Harare'),
  z('Africa/Windhoek', 'Namibia (Windhoek)', '+2', 'Harare'),
  z('Africa/Harare', 'Zimbabwe (Harare)', '+2', 'Harare'),
  z('Africa/Lusaka', 'Zambia (Lusaka)', '+2', 'Harare'),
  z('Africa/Maputo', 'Mozambique (Maputo)', '+2', 'Harare'),
  z('Africa/Cairo', 'Egypt (Cairo)', '+2', 'Cairo'),
  z('Africa/Nairobi', 'Kenya (Nairobi)', '+3', 'Nairobi'),
  z('Africa/Lagos', 'Nigeria (Lagos)', '+1', 'West Central Africa'),
  z('Indian/Mauritius', 'Mauritius (Port Louis)', '+4', 'Port Louis'),
  z('UTC', 'UTC', '0', 'GMT'),
  z('Europe/London', 'United Kingdom (London)', '0', 'United Kingdom(London)'),
  z('Europe/Dublin', 'Ireland (Dublin)', '0', 'Ireland(Dublin)'),
  z('Europe/Lisbon', 'Portugal (Lisbon)', '0', 'Portugal(Lisboa,Porto,Funchal)'),
  z('Europe/Amsterdam', 'Netherlands (Amsterdam)', '+1', 'Netherlands(Amsterdam)'),
  z('Europe/Berlin', 'Germany (Berlin)', '+1', 'Germany(Berlin)'),
  z('Europe/Paris', 'France (Paris)', '+1', 'France(Paris)'),
  z('Europe/Madrid', 'Spain (Madrid)', '+1', 'Spain(Madrid)'),
  z('Europe/Rome', 'Italy (Rome)', '+1', 'Italy(Rome)'),
  z('Europe/Athens', 'Greece (Athens)', '+2', 'Greece(Athens)'),
  z('Europe/Istanbul', 'Turkey (Istanbul)', '+3', 'Kuwait, Riyadh'),
  z('Europe/Moscow', 'Russia (Moscow)', '+3', 'Russia(Moscow)'),
  z('Asia/Riyadh', 'Saudi Arabia (Riyadh)', '+3', 'Kuwait, Riyadh'),
  z('Asia/Dubai', 'United Arab Emirates (Dubai)', '+4', 'Abu Dhabi, Muscat'),
  z('Asia/Karachi', 'Pakistan (Karachi)', '+5', 'Pakistan(Islamabad)'),
  z('Asia/Kolkata', 'India (Kolkata)', '+5:30', 'India(Calcutta)'),
  z('Asia/Singapore', 'Singapore', '+8', 'Singapore(Singapore)'),
  z('Asia/Hong_Kong', 'Hong Kong', '+8', 'Hong Kong, Urumqi'),
  z('Asia/Shanghai', 'China (Beijing)', '+8', 'China(Beijing)'),
  z('Asia/Tokyo', 'Japan (Tokyo)', '+9', 'Japan(Tokyo)'),
  z('Australia/Perth', 'Australia (Perth)', '+8', 'Australia(Perth)'),
  z('Australia/Adelaide', 'Australia (Adelaide)', '+9:30', 'Australia(Adelaide)'),
  z('Australia/Brisbane', 'Australia (Brisbane)', '+10', 'Australia(Brisbane)'),
  z('Australia/Sydney', 'Australia (Sydney, Melbourne)', '+10', 'Australia(Sydney,Melbourne,Canberra)'),
  z('Pacific/Auckland', 'New Zealand (Auckland)', '+12', 'New Zealand(Wellington,Auckland)'),
  z('America/Sao_Paulo', 'Brazil (São Paulo)', '-3', 'Brasilia'),
  z('America/Argentina/Buenos_Aires', 'Argentina (Buenos Aires)', '-3', 'Argentina(Buenos Aires)'),
  z('America/Toronto', 'Canada (Toronto)', '-5', 'Canada(Montreal,Ottawa,Quebec)'),
  z('America/New_York', 'US Eastern (New York)', '-5', 'United States-Eastern Time'),
  z('America/Chicago', 'US Central (Chicago)', '-6', 'United States-Central Time'),
  z('America/Denver', 'US Mountain (Denver)', '-7', 'United States-Mountain Time'),
  z('America/Phoenix', 'US Arizona (Phoenix)', '-7', 'Arizona'),
  z('America/Los_Angeles', 'US Pacific (Los Angeles)', '-8', 'United States-Pacific Time'),
];

export const PHONE_TIME_ZONE_IDS = PHONE_TIME_ZONES.map((t) => t.id) as [string, ...string[]];

export const phoneTimeZone = (id: string | null | undefined): PhoneTimeZone | null =>
  PHONE_TIME_ZONES.find((t) => t.id === id) ?? null;
